import type { Membership, PlayerProfile, RoomDirectory } from '@brush/api-client';
import { useActingRole } from '@brush/auth';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  onMembershipRecheck,
  type RecheckLatch,
  shouldRecheck,
  resolveMembership,
  revokeOutcome,
  type RevokedCard,
} from './membership';
import { clearPortalRoomId, loadPortalRoomId, savePortalRoomId } from './preferences';
import { useCurrentUser, usePlayerById, useRooms } from './queries';
import { refetchPortal } from './refetch';
import { portalSubject, type Browse } from './subject';
import { themeFor, type PortalTheme } from './theme';

interface PlayerContextValue {
  player: PlayerProfile | null;
  room: RoomDirectory | null;
  theme: PortalTheme;
  /** Every card this sign-in holds, in the order the server lists them (room, then card). */
  memberships: readonly Membership[];
  /** The one whose portal is on screen, when the portal is the caller's own. */
  membership: Membership | null;
  /** Which of your rooms the portal opens on. A preference — see `preferences.ts`. */
  selectRoom: (roomId: string) => void;
  /** Drop that preference, after disconnecting the room it named. */
  forgetRoom: (roomId: string) => void;
  /**
   * A card the room disconnected mid-session, until the player has been told about it (CR4).
   *
   * While it is set, `membership` is null whatever the list holds — which is what stops the
   * single-card fallback swapping rooms under somebody. `CardsScreen` renders the notice, and
   * `selectRoom` is the acknowledgement.
   */
  revokedCard: RevokedCard | null;
  /** Admins and operators may look up other players; a player may not. */
  canBrowse: boolean;
  /**
   * The portal on screen is somebody else's. Not "this caller could look somebody up": staff who
   * hold a card of their own read it as their own, and acting as a player is how they get there.
   */
  readOnly: boolean;
  selectPlayer: (id: string) => void;
  clearPlayer: () => void;
  isLoading: boolean;
  isError: boolean;
  /** The first of the portal's three reads to fail — who you are, the rooms, the player — for the error card (#230). */
  error: unknown;
  refetch: () => void;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used within a PlayerProvider.');
  return ctx;
}

/**
 * Resolves whose portal is on screen — from `/api/me` and nothing else.
 *
 * <b>A player resolves to their own card through `memberships`</b> (ADR 0010, Decision 6), which
 * closes known gap 1's client half. There is one source of truth for what somebody may see, for
 * the reason ADR 0009 gives about `availableRoles`: a second endpoint answering the same question
 * is a second thing to disagree, and the app would then offer a room the server refused.
 *
 * <b>Staff browse; players read their own, and the acting role is what decides which.</b> That is
 * not a fallback order — it is the whole rule, and it is why an account holding both a role and a
 * card needs no affordance of its own. Narrowing to `player` drops both staff claims, so
 * `canBrowse` goes false and the memberships path resolves; acting as staff, the landing is the
 * picker exactly as before. ADR 0009 predicted this is what would make acting as a player useful,
 * and the switcher it already built is the control for it. The alternative — showing staff their
 * own card automatically when they happen to hold one — would make the app's subject depend on a
 * fact about the person rather than on a choice they made.
 *
 * `VITE_PLAYER_ID` is gone with this. It pinned the portal to one player in development because
 * nothing linked a sign-in to a record; a claim under the dev bypass now does the same job through
 * the code the product actually runs.
 *
 * <b>And because `/api/me` is the only source, this is also where a card being taken away has to
 * be noticed</b> (CR4 of `docs/manual-tests/13-fantasy-walk.md`). The server refuses the moment a
 * room revokes, but nothing was re-asking, so the portal went on naming the holder and navigating
 * until a hard refresh. The rules are in `membership.ts`; what lives here is the state they
 * produce — see the denial effect below.
 */
export function PlayerProvider({ children }: { children: ReactNode }) {
  const meQuery = useCurrentUser();
  const roomsQuery = useRooms();
  const { actingRole } = useActingRole();

  // Read once, at mount, for the reason room-web's default room is: a change made in another tab
  // must not move the subject of the page out from under whatever is on screen here.
  const [chosenRoomId, setChosenRoomId] = useState<string | null>(() => loadPortalRoomId());
  // The player staff chose to look at, with the role they chose it under: portalSubject ends the browse when that
  // role is no longer in force (#526).
  const [browse, setBrowse] = useState<Browse | null>(null);
  const [revokedCard, setRevokedCard] = useState<RevokedCard | null>(null);

  const me = meQuery.data;
  const canBrowse = !!me && (me.isAdmin || me.isRoomOperator);
  const memberships = useMemo<readonly Membership[]>(() => me?.memberships ?? [], [me]);

  // `resolveMembership` carries the rule and its reasoning; it is in `membership.ts` because the
  // revoke branch has to run it against the list before a revoke and the list after one, and two
  // copies of it would disagree in the direction that lets a silent room swap through.
  //
  // A revoke suppresses it outright until the player has been told. That is not belt and braces:
  // the fallback to a sole remaining card is exactly what would move somebody from the standings
  // they were reading into another room's, and the refetch below is what would trigger it.
  const membership = useMemo(
    () => (revokedCard === null ? resolveMembership(memberships, chosenRoomId) : null),
    [memberships, chosenRoomId, revokedCard],
  );

  const forgetRoom = useCallback(
    (roomId: string) => {
      if (chosenRoomId !== roomId) return;
      clearPortalRoomId();
      setChosenRoomId(null);
    },
    [chosenRoomId],
  );

  const selectRoom = useCallback((roomId: string) => {
    savePortalRoomId(roomId);
    setChosenRoomId(roomId);
    setBrowse(null);
    // Choosing a room is the acknowledgement, and the reason the notice needs no dismiss control
    // of its own: CR4 asks for it to be said once, and there is nothing left to say once somebody
    // has picked which of their remaining cards to carry on with. Connecting a new card ends here
    // too — `CardsScreen` selects the room it was connected at.
    setRevokedCard(null);
  }, []);

  /**
   * CR4's signal, and the whole of what happens on it.
   *
   * <b>A failure is the cue to recheck `/api/me`, and only the cue.</b> `triggersMembershipRecheck`
   * says which failures — a membership 403, or any 404, since an entity in a room the caller can no
   * longer read now answers like one that does not exist — and `shouldRecheck` says when: never
   * while a check is out, and not within ten seconds of the last one finishing. A screen opening
   * four queries fails four times in a burst and costs one check.
   *
   * <b>The latch is time, not the membership list.</b> It latched on the list's identity, so a
   * check that found nothing gone — the list unchanged, the same reference under TanStack's
   * structural sharing — switched every later check off for the session. A failed refetch records
   * no finish time, so a transient error never holds the next check back.
   *
   * <b>The decision is a diff, never the error.</b> The failure says only "not here" — a 404 says
   * less than that, and the bare policy refusal does not even say which room — so what a revoke
   * *was* is read off the two membership lists. That also keeps a 404 for a contest somebody deleted
   * from claiming a disconnection nobody made, and it is why a failed refetch is silent: the
   * previous data is still in hand, the diff is empty, and reporting a revoke we could not confirm
   * is the one wrong answer available.
   *
   * `refetch` rather than `invalidateQueries`, because the result is the second half of the diff
   * and asking the cache for it afterwards would be a race with the write that fills it.
   */
  const latch = useRef<RecheckLatch>({ inFlight: false, lastFinishedAt: null });
  const refetchMe = meQuery.refetch;

  useEffect(
    () =>
      onMembershipRecheck(() => {
        if (!shouldRecheck(latch.current, Date.now())) return;
        latch.current = { inFlight: true, lastFinishedAt: latch.current.lastFinishedAt };

        const before = memberships;
        void refetchMe().then((result) => {
          if (result.isError) {
            latch.current = { inFlight: false, lastFinishedAt: null };
            return;
          }
          latch.current = { inFlight: false, lastFinishedAt: Date.now() };

          const outcome = revokeOutcome({
            before,
            after: result.data?.memberships ?? [],
            chosenRoomId,
          });
          // `unchanged` and `quiet` are both "say nothing" — and in the second case the refreshed
          // list is already on screen, which is the whole of what CR4 asks for there.
          if (outcome.kind !== 'announce') return;

          // The preference names a card this sign-in no longer holds. `forgetRoom` self-guards, so
          // the cards-list case — where nothing was stored to begin with — passes straight over.
          forgetRoom(outcome.roomId);
          setRevokedCard(outcome);
        });
      }),
    [memberships, chosenRoomId, refetchMe, forgetRoom],
  );

  // A browse made under another role is over, not paused: switching back to that role must not bring it back.
  // `portalSubject` already ignores it for this render; this drops it for good (React's adjust-state-while-rendering).
  if (browse !== null && browse.role !== actingRole) setBrowse(null);
  const subject = portalSubject({ canBrowse, actingRole, browse, membershipPlayerId: membership?.playerId ?? null });
  const playerQuery = usePlayerById(subject.playerId);

  const player = playerQuery.data ?? null;
  const room = player ? (roomsQuery.data ?? []).find((r) => r.id === player.roomId) ?? null : null;

  const value = useMemo<PlayerContextValue>(
    () => ({
      player,
      room,
      theme: themeFor(room),
      memberships,
      // Null while staff are browsing, even where the two happen to name the same card: what is on
      // screen is then somebody's record being looked up, not a card being held.
      membership: subject.readOnly ? null : membership,
      selectRoom,
      forgetRoom,
      revokedCard,
      canBrowse,
      // What makes a portal read-only is that it is somebody else's, not that the caller could
      // look somebody else up. Staff who hold a card of their own read it as their own.
      readOnly: subject.readOnly,
      selectPlayer: (id: string) => setBrowse({ id, role: actingRole }),
      clearPlayer: () => setBrowse(null),
      isLoading: meQuery.isLoading || roomsQuery.isLoading || playerQuery.isLoading,
      isError: meQuery.isError || roomsQuery.isError || playerQuery.isError,
      error: meQuery.error ?? roomsQuery.error ?? playerQuery.error ?? null,
      refetch: () => refetchPortal({ me: meQuery, rooms: roomsQuery, player: playerQuery }, subject.playerId),
    }),
    [
      player,
      room,
      memberships,
      membership,
      subject.readOnly,
      subject.playerId,
      actingRole,
      revokedCard,
      selectRoom,
      forgetRoom,
      canBrowse,
      meQuery,
      roomsQuery,
      playerQuery,
    ],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}
